<script setup lang="ts">
import { motion } from 'motion-v'
import HeroSection from '@/components/HeroSection.vue'
import ServicesSection from '@/components/ServicesSection.vue'
</script>

<template>
  <HeroSection />

  <section id="tools" class="tools" aria-labelledby="tools-heading">
    <header class="tools-header">
      <p class="section-marker">Free checkups</p>
      <h2 id="tools-heading" class="tools-heading display">
        Two quick scans you can run on any site.
      </h2>
      <p class="tools-lede">
        Drop in your address (or a competitor's). The report comes back on screen in about fifteen
        seconds — what's working, what's broken, what I'd fix first. No signup, and nothing gets
        emailed unless you ask for it.
      </p>
    </header>

    <div class="tool-grid">
      <motion.article
        class="tool-card"
        :initial="{ opacity: 0, y: 16 }"
        :animate="{ opacity: 1, y: 0 }"
        :transition="{ duration: 0.55, ease: 'easeOut' }"
      >
        <div class="tool-head">
          <span class="tool-badge">Security</span>
          <span class="tool-runtime">~15 seconds</span>
        </div>
        <h3 class="tool-title">Is my website safe?</h3>
        <p class="tool-body">
          Five checks: whether your certificate is valid and how many days before it expires,
          whether HTTPS is actually enforced, which of the six security headers browsers look for
          are set, where your DNS points, and whether anything is reachable that shouldn't be.
        </p>
        <ul class="tool-list" aria-label="Security checks">
          <li>HTTPS &amp; certificate health</li>
          <li>Security headers</li>
          <li>DNS &amp; redirect hygiene</li>
        </ul>
        <RouterLink class="tool-cta" to="/security">
          <span>Check my website</span>
          <span class="tool-cta-arrow" aria-hidden="true">→</span>
        </RouterLink>
      </motion.article>

      <motion.article
        class="tool-card"
        :initial="{ opacity: 0, y: 16 }"
        :animate="{ opacity: 1, y: 0 }"
        :transition="{ duration: 0.55, delay: 0.12, ease: 'easeOut' }"
      >
        <div class="tool-head">
          <span class="tool-badge">Local search</span>
          <span class="tool-runtime">~15 seconds</span>
        </div>
        <h3 class="tool-title">Can people find me on Google?</h3>
        <p class="tool-body">
          When somebody in Price or Castle Dale searches for what you do, do you turn up — or does a
          Salt Lake competitor? Eight checks covering what decides whether you land on page one or
          page five for the searches that actually bring in calls.
        </p>
        <ul class="tool-list" aria-label="SEO checks">
          <li>Title, meta &amp; heading structure</li>
          <li>Local-business markup</li>
          <li>Mobile readiness &amp; sitemap</li>
        </ul>
        <RouterLink class="tool-cta" to="/seo">
          <span>Check my listing</span>
          <span class="tool-cta-arrow" aria-hidden="true">→</span>
        </RouterLink>
      </motion.article>
    </div>
  </section>

  <ServicesSection />
</template>

<style scoped>
.tools {
  margin: 3rem 0 2rem;
  padding: 2.75rem 0 1rem;
  position: relative;
}

.tools::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    var(--border) 8%,
    var(--border-bright) 50%,
    var(--border) 92%,
    transparent 100%
  );
}

.tools-header {
  max-width: 38em;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  margin-bottom: 2.25rem;
}

.tools-heading {
  margin: 0;
  font-size: clamp(1.7rem, 3.4vw, 2.4rem);
  font-weight: 400;
  color: var(--text);
  letter-spacing: -0.015em;
  line-height: 1.1;
}

.tools-heading em {
  font-style: italic;
  color: var(--accent-bright);
  font-variation-settings: 'opsz' 96;
}

.tools-lede {
  margin: 0;
  font-size: 1.05rem;
  line-height: 1.6;
  color: var(--text-muted);
  font-variation-settings: 'opsz' 18;
}

.tool-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(310px, 1fr));
  gap: 1.5rem;
}

.tool-card {
  position: relative;
  background: linear-gradient(180deg, var(--surface) 0%, var(--surface-2) 100%);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 1.75rem 1.6rem 1.6rem;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  transition:
    border-color 0.25s ease,
    transform 0.25s ease,
    box-shadow 0.25s ease;
  overflow: hidden;
}
.tool-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 0;
  height: 1px;
  background: var(--accent);
  transition: width 0.4s ease;
}
.tool-card:hover {
  border-color: var(--border-bright);
  transform: translateY(-2px);
  box-shadow: var(--shadow-lg);
}
.tool-card:hover::before {
  width: 100%;
}

.tool-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  font-family: var(--font-mono);
  font-size: 0.66rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--text-faint);
  border-bottom: 1px solid var(--border);
  padding-bottom: 0.85rem;
  margin-bottom: 0.25rem;
}

.tool-badge {
  color: var(--accent-bright);
}
.tool-runtime {
  color: var(--text-muted);
}

.tool-title {
  margin: 0;
  font-family: var(--font-display);
  font-size: 1.5rem;
  font-weight: 500;
  letter-spacing: -0.01em;
  color: var(--text);
  font-variation-settings: 'opsz' 36;
  line-height: 1.2;
}

.tool-body {
  margin: 0;
  color: var(--text-muted);
  flex-grow: 1;
  font-size: 1rem;
  line-height: 1.6;
}

.tool-list {
  list-style: none;
  margin: 0.25rem 0 0.5rem;
  padding: 0;
  display: grid;
  gap: 0.4rem;
}
.tool-list li {
  position: relative;
  padding-left: 1.25rem;
  font-family: var(--font-mono);
  font-size: 0.78rem;
  letter-spacing: 0.02em;
  color: var(--text);
}
.tool-list li::before {
  content: '✓';
  position: absolute;
  left: 0;
  top: -0.05em;
  color: var(--accent);
  font-family: var(--font-display);
  font-weight: 600;
}

.tool-cta {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.7rem 1.2rem;
  margin-top: 0.5rem;
  background: var(--accent);
  color: var(--on-accent);
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 0.98rem;
  border-radius: var(--radius-sm);
  text-decoration: none;
  border: 1px solid var(--accent);
  transition:
    background 0.2s ease,
    transform 0.2s ease;
}
.tool-cta:hover {
  background: var(--accent-bright);
  text-decoration: none;
}
.tool-cta-arrow {
  display: inline-block;
  transition: transform 0.25s ease;
}
.tool-cta:hover .tool-cta-arrow {
  transform: translateX(3px);
}

@media (max-width: 600px) {
  .tools {
    padding-top: 2rem;
  }
  .tool-card {
    padding: 1.4rem 1.25rem 1.4rem;
  }
}
</style>
